import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Landmark, Banknote, CreditCard, Smartphone, Pencil, Trash2, Loader2 } from "lucide-react";
import "./ReceivingAccountsOverlay.css";

const TYPE_META = {
  cash: { icon: Banknote, label: "Cash", accent: "cash" },
  POS: { icon: CreditCard, label: "POS Terminal", accent: "pos" },
  bankTransfer: { icon: Landmark, label: "Bank Transfer", accent: "bank" },
  mobilePay: { icon: Smartphone, label: "Mobile Pay", accent: "mobile" },
  card: { icon: CreditCard, label: "Card", accent: "pos" },
  storelensePOS: { icon: CreditCard, label: "StoreLense POS", accent: "pos" },
};

function AccountDetailLines(account) {
  switch (account.paymentType) {
    case "bankTransfer":
      return [account.bankName, account.accountNumber].filter(Boolean);
    case "POS":
      return [account.provider, account.terminalId ? `Terminal ID: ${account.terminalId}` : null].filter(Boolean);
    case "mobilePay":
      return [account.provider, account.walletNumber].filter(Boolean);
    case "cash":
      return [account.provider].filter(Boolean);
    default:
      return [account.provider].filter(Boolean);
  }
}

function AccountRow({ account, onEdit, onDelete }) {
  const meta = TYPE_META[account.paymentType] || TYPE_META.cash;
  const Icon = meta.icon;
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const detailLines = AccountDetailLines(account);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete?.(account._id);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <li className='rao-row'>
      <span className={`rao-row__icon rao-row__icon--${meta.accent}`}>
        <Icon size={18} strokeWidth={2.25} />
      </span>

      <div className='rao-row__body'>
        <span className='rao-row__name'>{account.name}</span>
        {detailLines.length > 0 && <span className='rao-row__detail'>{detailLines.join(" • ")}</span>}
        <div className='rao-row__tags'>
          {account.isPartner?.hasIntgration && <span className='rao-row__partner-tag'>Partner</span>}
          {account.active === false && <span className='rao-row__inactive'>Inactive</span>}
        </div>
      </div>

      {confirming ? (
        <div className='rao-row__confirm'>
          <span className='rao-row__confirm-text'>Delete?</span>
          <button type='button' className='rao-row__confirm-btn rao-row__confirm-btn--danger' onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader2 size={13} className='rao-spin' /> : "Yes"}
          </button>
          <button type='button' className='rao-row__confirm-btn' onClick={() => setConfirming(false)} disabled={deleting}>
            No
          </button>
        </div>
      ) : (
        <div className='rao-row__actions'>
          <button type='button' className='rao-row__action-btn' onClick={() => onEdit(account)}>
            <Pencil size={14} strokeWidth={2.25} />
            Edit
          </button>
          <button type='button' className='rao-row__action-btn rao-row__action-btn--danger' onClick={() => setConfirming(true)}>
            <Trash2 size={14} strokeWidth={2.25} />
            Delete
          </button>
        </div>
      )}
    </li>
  );
}

export default function ReceivingAccountsOverlay({ accounts = [], onClose, onAdd, onEdit, onDelete }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <div className='rao-backdrop' onMouseDown={onClose}>
      <div className='rao-panel' onMouseDown={(e) => e.stopPropagation()}>
        <div className='rao-panel__header'>
          <h3 className='rao-panel__title'>Payment Accounts</h3>
          <button type='button' className='rao-panel__close' onClick={onClose} aria-label='Close'>
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        <button type='button' className='rao-panel__add-btn' onClick={onAdd}>
          <Plus size={16} strokeWidth={2.5} />
          Add Payment Account
        </button>

        {accounts.length === 0 ? (
          <div className='rao-panel__empty'>
            <p>No payment accounts yet. Add one to start receiving payments.</p>
          </div>
        ) : (
          <ul className='rao-panel__list'>
            {accounts.map((account) => (
              <AccountRow key={account._id} account={account} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}
